import { useState, useEffect } from "react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useNavigate } from "@tanstack/react-router";
import { CommonInstallflowFrame } from "./common-install-flow-frame";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Spinner } from "../ui/spinner";
import { Navigation2 } from "lucide-react";
import { AddressAutofill } from "@mapbox/search-js-react";
import { Combobox } from "@/components/combo-box";
import { US_STATES, getStateLabel } from "@/location/constants";
import { installFlowAddress } from "@/installFlow/storage-keys";

const addressFormSchema = z.object({
  streetAddress: z.string().min(1, "Street address is required"),
  aptSuiteUnit: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zipCode: z
    .string()
    .min(1, "ZIP code is required")
    .regex(/^\d{5}(-\d{4})?$/, "Please enter a valid ZIP code"),
});

interface AuthProps extends React.HTMLAttributes<HTMLDivElement> {}

export type LocationPermissionResult =
  | { ok: true; latitude: number; longitude: number }
  | { ok: false; error: string };

/**
 * Asks the browser for permission to access the device's GPS location.
 * Resolves with coordinates on success, or with an error message if denied/unavailable.
 */
export function requestLocationAccess(): Promise<LocationPermissionResult> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({
        ok: false,
        error: "Geolocation is not supported by this browser.",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          ok: true,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (err) => {
        const message =
          err.code === err.PERMISSION_DENIED
            ? "Location permission was denied."
            : err.code === err.POSITION_UNAVAILABLE
              ? "Location is unavailable."
              : err.code === err.TIMEOUT
                ? "Location request timed out."
                : "An unknown error occurred.";
        resolve({ ok: false, error: message });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

export function AddNewLocation({ className }: AuthProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationGranted, setLocationGranted] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [currentLocationAddress, setCurrentLocationAddress] = useState<{
    formatted: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  } | null>(null);
  const [showCurrentLocation, setShowCurrentLocation] = useState(true);

  const MAPBOX_API_KEY = import.meta.env.VITE_MAPBOX_API_KEY as
    | string
    | undefined;

  const form = useForm<z.infer<typeof addressFormSchema>>({
    resolver: zodResolver(addressFormSchema),
    mode: "onChange",
    defaultValues: {
      streetAddress: "",
      aptSuiteUnit: "",
      city: "",
      state: "",
      zipCode: "",
    },
  });

  // Request location permission on mount
  useEffect(() => {
    requestLocationAccess().then((result) => {
      if (result.ok) {
        setLocationGranted({
          latitude: result.latitude,
          longitude: result.longitude,
        });
        setLocationError(null);
        sessionStorage.setItem(
          "installFlowUserCoords",
          JSON.stringify({ lat: result.latitude, lng: result.longitude }),
        );
      } else {
        setLocationError(result.error);
        setLocationGranted(null);
      }
    });
  }, []);

  // Reverse geocode current coordinates via Mapbox
  useEffect(() => {
    if (!locationGranted || !MAPBOX_API_KEY) return;

    const { latitude, longitude } = locationGranted;
    fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?types=address&access_token=${MAPBOX_API_KEY}`,
    )
      .then((res) => res.json())
      .then((data) => {
        const feature = data.features?.[0];
        if (!feature) return;

        const ctx = (feature.context ?? []) as Array<{
          id: string;
          text: string;
          short_code?: string;
        }>;
        const getCtx = (prefix: string) =>
          ctx.find((c) => c.id.startsWith(prefix));

        const place = getCtx("place");
        const region = getCtx("region");
        const postcode = getCtx("postcode");

        setCurrentLocationAddress({
          formatted: feature.place_name ?? "",
          streetAddress: feature.place_name?.split(",")[0] ?? "",
          city: place?.text,
          state: region?.short_code?.replace("US-", "") ?? region?.text,
          zipCode: postcode?.text,
        });
      })
      .catch(() => {
        // Silently fail — user can still type manually
      });
  }, [locationGranted, MAPBOX_API_KEY]);

  // Called by Mapbox AddressAutofill when the user selects a suggestion
  function handleAutofillRetrieve(response: any) {
    const feature = response.features?.[0];
    if (!feature) return;
    const { properties } = feature;

    form.setValue(
      "streetAddress",
      properties.address_line1 || form.getValues("streetAddress"),
      { shouldValidate: true },
    );
    form.setValue(
      "city",
      properties.address_level2 || form.getValues("city"),
      { shouldValidate: true },
    );
    form.setValue(
      "state",
      (properties.address_level1 || "").slice(0, 2).toUpperCase(),
      { shouldValidate: true },
    );
    form.setValue(
      "zipCode",
      properties.postcode || form.getValues("zipCode"),
      { shouldValidate: true },
    );

    setShowCurrentLocation(false);
  }

  async function onSubmit(values: z.infer<typeof addressFormSchema>) {
    setIsSubmitting(true);
    try {
      installFlowAddress.set(JSON.stringify(values));
      await navigate({ to: "/installFlow/create-asset-location" });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <CommonInstallflowFrame>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4 w-full"
        >
          <div className="grid gap-6">
            <h1 className="text-xl font-semibold text-foreground">
              What&apos;s the address?
            </h1>

            <FormField
              control={form.control}
              name="streetAddress"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormControl>
                    <div className="relative">
                      {/* @ts-expect-error - Mapbox AddressAutofill has React 18 type compatibility issues */}
                      <AddressAutofill
                        accessToken={MAPBOX_API_KEY ?? ""}
                        onRetrieve={handleAutofillRetrieve}
                      >
                        <input
                          type="text"
                          placeholder="Street address"
                          autoComplete="address-line1"
                          className="h-12 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            if (e.target.value) setShowCurrentLocation(false);
                            else setShowCurrentLocation(true);
                          }}
                          onBlur={field.onBlur}
                        />
                      </AddressAutofill>
                    </div>
                  </FormControl>

                  {showCurrentLocation && currentLocationAddress && (
                    <div className="rounded-2xl border border-border bg-card shadow-md">
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left hover:bg-accent"
                        onClick={() => {
                          const addr = currentLocationAddress;
                          field.onChange(addr.streetAddress || addr.formatted);
                          if (addr.city) {
                            form.setValue("city", addr.city, {
                              shouldValidate: true,
                            });
                          }
                          if (addr.state) {
                            form.setValue("state", addr.state, {
                              shouldValidate: true,
                            });
                          }
                          if (addr.zipCode) {
                            form.setValue("zipCode", addr.zipCode, {
                              shouldValidate: true,
                            });
                          }
                          setShowCurrentLocation(false);
                        }}
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Navigation2 className="h-4 w-4" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-primary">
                            Current location
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {currentLocationAddress.formatted}
                          </span>
                        </div>
                      </button>
                    </div>
                  )}

                  {locationError && (
                    <p className="text-xs text-muted-foreground">
                      {locationError}
                    </p>
                  )}

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="aptSuiteUnit"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Apt, suite, or unit"
                      autoComplete="address-line2"
                      className="h-12 rounded-lg"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="City"
                      autoComplete="address-level2"
                      className="h-12 rounded-lg"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Combobox
                        placeholder="Select state..."
                        options={US_STATES}
                        onSelect={field.onChange}
                        defaultLabel={getStateLabel(field.value)}
                        className="w-full h-12"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="ZIP code"
                        autoComplete="postal-code"
                        className="h-12 rounded-lg"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button
              type="submit"
              variant="installFlow"
              className="w-full h-[50px] p-4 rounded-[25px]"
              disabled={!form.formState.isValid || isSubmitting}
            >
              {isSubmitting ? <Spinner className="h-6 w-6" /> : "Next"}
            </Button>
          </div>
        </form>
      </Form>
    </CommonInstallflowFrame>
  );
}
